<template>
  <UserLayout :extra-breadcrumbs="breadcrumbs">
    <template #sidebar="{ selectedCallback }">
      <DashboardSidebarContentSection>
        <DashboardSidebarSection title="Areas">
          <VFallbackText v-if="!patchOptions || patchOptions.length === 0">
            No areas set
          </VFallbackText>
          <VRadioControl
            v-else
            :model-value="selectedPatchId"
            name="patch"
            :options="patchOptions"
            radio-class="sidebar"
            @update:model-value="(patch: string | number | boolean) => onPatchSelected(patch as number, selectedCallback)"
          />
        </DashboardSidebarSection>
      </DashboardSidebarContentSection>
    </template>
    <teleport
      v-if="listingsStore.isPitchPanelMapEnabled"
      :to="listingsStore.isPitchPanelReady ? '#google-map-teleport' : '#google-map-default-slot'"
    >
      <PropertyDetailsMediaMap
        :gmap-api-key="gmapApiKey"
        :lat="listingsStore.pitchPanelMapCoords?.lat ?? PLACEHOLDER_LAT"
        :lon="listingsStore.pitchPanelMapCoords?.lon ?? PLACEHOLDER_LON"
      />
    </teleport>
    <teleport
      v-if="listingsStore.isPitchPanelStreetViewEnabled"
      :to="
        listingsStore.isPitchPanelReady
          ? '#street-view-teleport'
          : '#google-street-view-default-slot'
      "
    >
      <PropertyDetailsMediaStreetView
        :gmap-api-key="gmapApiKey"
        :lat="listingsStore.pitchPanelMapCoords?.lat ?? PLACEHOLDER_LAT"
        :lon="listingsStore.pitchPanelMapCoords?.lon ?? PLACEHOLDER_LON"
      />
    </teleport>
    <VFallbackText v-if="!hasAnyAreas" class="centered">
      No areas assigned to the user
    </VFallbackText>
    <slot v-else />
  </UserLayout>
</template>

<script lang="ts" setup>
  // Types
  import type { Router } from 'vue-router';
  import type { TAuthStore } from '~/modules/auth/store/types';
  import type { TPatch } from '~/misc/auth/types';
  import type { TListingsStore } from '~/misc/listings/store/types';
  import type { TListingOpportunity } from '~/misc/opportunity/types';
  import type { TRadioControl } from '~/misc/v-radio-control/types';
  import { EAnalyticsEvent } from '~/misc/services/analytics/types';
  import { ESaleType } from '~/misc/listings/types';

  // Utils
  import { eventBus } from '~/misc/utils/event-bus';

  // Store
  import { useAuthStore } from '~/modules/auth/store';
  import { useListingsStore } from '~/misc/listings/store';

  // Config
  import { mapLibConfigInjectionKey } from '~/misc/v-map/config';
  import { PLACEHOLDER_LAT, PLACEHOLDER_LON } from '~/layouts/user/search/config';

  // Composables
  import { useOpportunitiesStoreToQueryResolver } from '~/modules/opportunities/composables/use-store-to-query-resolver';

  // Components
  import DashboardSidebarContentSection from '~/modules/dashboard/sidebar/content-section/DashboardSidebarContentSection.vue';
  import UserLayout from '~/layouts/user/index.vue';
  import VRadioControl from '~/misc/v-radio-control/VRadioControl.vue';
  import DashboardSidebarSection from '~/modules/dashboard/sidebar/section/DashboardSidebarSection.vue';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import PropertyDetailsMediaMap from '~/misc/opportunity/property-details/media/map/PropertyDetailsMediaMap.vue';
  import PropertyDetailsMediaStreetView from '~/misc/opportunity/property-details/media/street-view/PropertyDetailsMediaStreetView.vue';

  const PropertyModal = defineAsyncComponent(
    () => import('~/misc/property/modal/PropertyModal.vue')
  );

  const config = useRuntimeConfig();

  const gmapApiKey: string | undefined = config.public.GMAP_API_KEY;

  if (typeof gmapApiKey === 'string') {
    const libraries: string | boolean | undefined = config.public.GMAP_API_LIBRARIES;

    provide(mapLibConfigInjectionKey, {
      apiKey: gmapApiKey,
      libraries: typeof libraries === 'string' ? libraries : '',
    });
  }

  const router: Router = useRouter();
  const route = useRoute();

  useOpportunitiesStoreToQueryResolver(router);

  const showAllPatches: ComputedRef<boolean> = computed<boolean>(
    () => route.meta.showAllPatches === true
  );

  const authStore: TAuthStore = useAuthStore();
  const listingsStore: TListingsStore = useListingsStore();
  const { show: showModal } = useModal();

  const selectedPatch: ComputedRef<TPatch | undefined> = computed<TPatch | undefined>(
    () => listingsStore.selectedPatch
  );

  const selectedPatchId: ComputedRef<number | undefined> = computed<number | undefined>(
    () => selectedPatch.value?.id
  );

  const currentSaleType: ComputedRef<ESaleType> = computed<ESaleType>(
    () => listingsStore.filters.type
  );

  const patches: ComputedRef<TPatch[]> = computed<TPatch[]>(() => {
    const allPatches: TPatch[] = authStore.patches;

    if (showAllPatches.value) {
      return allPatches;
    }

    const currentSaleTypeValue: ESaleType = currentSaleType.value;

    return allPatches.filter((patch: TPatch) => {
      if (patch.type === 'both') return true;

      if (currentSaleTypeValue === ESaleType.SALES && patch.type === 'sale') return true;

      if (currentSaleTypeValue === ESaleType.LETTINGS && patch.type === 'let') return true;

      return false;
    });
  });

  const selectedPatchLabel: ComputedRef<string> = computed<string>(
    () => selectedPatch.value?.name ?? ''
  );

  const breadcrumbs: ComputedRef<string[]> = computed<string[]>(() => [selectedPatchLabel.value]);

  const selectedOpportunity: ComputedRef<TListingOpportunity | null> =
    computed<TListingOpportunity | null>(() => listingsStore.selectedOpportunity);

  const patchOptions: ComputedRef<TRadioControl[] | undefined> = computed<
    TRadioControl[] | undefined
  >(() =>
    patches.value?.map((patch: TPatch): TRadioControl => ({ label: patch.name, value: patch.id }))
  );

  const hasAnyAreas: ComputedRef<boolean> = computed<boolean>(
    () => authStore.patches.length > 0
  );

  const onPatchSelected = (value: number | undefined, selectedCallback: Function) => {
    const selectedPatch: TPatch | undefined = value
      ? patches.value?.find((patch: TPatch) => patch.id === value)
      : undefined;

    if (selectedPatch) {
      eventBus.emit(EAnalyticsEvent.DASHBOARD_SIDEBAR_AREA_CHANGED, {
        value: selectedPatch,
      });
    }

    setPatch(selectedPatch);
    selectedCallback();
  };

  const setPatch = (value: TPatch | undefined) => {
    listingsStore.setSelectedPatch(value);
  };

  watch(
    () => patches.value,
    (filteredPatches: TPatch[]) => {
      const isSelectedPatchValid: boolean | undefined =
        selectedPatch.value &&
        filteredPatches.some((patch: TPatch) => patch.id === selectedPatch.value?.id);

      if (isSelectedPatchValid) {
        return;
      }

      setPatch(filteredPatches[0]);
    },
    {
      immediate: true,
    }
  );

  const resetSelectedOpportunity = () => listingsStore.setSelectedOpportunity(null);

  watch(selectedOpportunity, (data: TListingOpportunity | null) => {
    const propertyId: number | null | undefined = data?.property_id;

    if (propertyId === undefined || propertyId === null) return;

    showModal(
      PropertyModal,
      { title: data?.address, propertyId: data?.property_id },
      {},
      { 'after-close': resetSelectedOpportunity }
    );
  });
</script>
